import {
	AgentChatWidgetQueryService,
	type SystemAgentWidgetData,
} from '@supply-mover-2-0/core';
import { Capabilities, CapabilityService } from '@supply-mover-2-0/shared';
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
	type ReactNode,
} from 'react';

interface AgentChatDataContextValue {
	systemAgentData: SystemAgentWidgetData | null;
	isLoading: boolean;
	refresh: () => Promise<void>;
}

const AgentChatDataContext = createContext<AgentChatDataContextValue | null>(
	null,
);

/**
 * PD-16917 — the in-app assistant is an AI agent, so the widget needs the
 * ai_agents capability as well as an assistant resolved for the user (their
 * own pick, their role's or the tenant's; the server decides, PD-16920).
 * Without the capability the widget is disabled and no request is made. The
 * capability list is loaded before this provider mounts.
 */
const NO_AI_AGENTS: SystemAgentWidgetData = { enabled: false, agent: null };

function aiAgentsAllowed(): boolean {
	return CapabilityService.allows(Capabilities.AI_AGENTS);
}

export interface AgentChatDataProviderProps {
	children: ReactNode;
}

/**
 * Provider that pre-fetches the system AI agent for the chat widget at app
 * initialization. This ensures the agent data is immediately available when
 * the navigation menu opens — avoids a flash of missing widget on first paint.
 */
export const AgentChatDataProvider: React.FC<AgentChatDataProviderProps> = ({
	children,
}) => {
	const [systemAgentData, setSystemAgentData] =
		useState<SystemAgentWidgetData | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	const refresh = useCallback(async () => {
		if (!aiAgentsAllowed()) {
			setSystemAgentData(NO_AI_AGENTS);
			setIsLoading(false);
			return;
		}
		setIsLoading(true);
		try {
			const data = await AgentChatWidgetQueryService.getSystemAgent(true);
			setSystemAgentData(data);
		} finally {
			setIsLoading(false);
		}
	}, []);

	// PD-15747 — one failed startup fetch must not hide the launcher for the
	// whole session. In the native shell the first request regularly races
	// auth restore at cold start (and field devices lose connectivity), so a
	// fetchFailed result is retried with backoff until a real answer —
	// enabled or genuinely disabled — arrives.
	useEffect(() => {
		let cancelled = false;
		let timer: ReturnType<typeof setTimeout> | null = null;
		const RETRY_DELAYS_MS = [2_000, 5_000, 15_000, 60_000];

		if (!aiAgentsAllowed()) {
			setSystemAgentData(NO_AI_AGENTS);
			setIsLoading(false);
			return undefined;
		}

		const attempt = (attemptIndex: number): void => {
			void AgentChatWidgetQueryService.getSystemAgent(attemptIndex > 0).then(
				(data) => {
					if (cancelled) {
						return;
					}
					setSystemAgentData(data);
					setIsLoading(false);
					if (
						data.fetchFailed === true &&
						attemptIndex < RETRY_DELAYS_MS.length
					) {
						timer = setTimeout(() => {
							attempt(attemptIndex + 1);
						}, RETRY_DELAYS_MS[attemptIndex]);
					}
				},
			);
		};
		attempt(0);

		return (): void => {
			cancelled = true;
			if (timer != null) {
				clearTimeout(timer);
			}
		};
	}, []);

	return (
		<AgentChatDataContext.Provider
			value={{
				systemAgentData,
				isLoading,
				refresh,
			}}
		>
			{children}
		</AgentChatDataContext.Provider>
	);
};

/**
 * Hook to access pre-fetched agent chat data.
 * Returns null if not within an AgentChatDataProvider.
 */
export const useAgentChatData = (): AgentChatDataContextValue | null =>
	useContext(AgentChatDataContext);

/**
 * Hook to safely access pre-fetched agent chat data.
 * This is an alias for useAgentChatData for consistency with other safe hooks.
 */
export const useAgentChatDataSafe = (): AgentChatDataContextValue | null =>
	useContext(AgentChatDataContext);
